import { useState, useEffect, useCallback } from "react";
import { getTask, getOperationalDocument, updateTaskCostLine, type TaskDetail, type OperationalDocumentSummary, type CostLineOut } from "../../../api/tasks";
import { TaskTimeline } from "./TaskTimeline";
import { TaskHistory } from "./TaskHistory";
import { Tabs } from "../../primitives/Tabs";

type TaskDetailViewProps = {
  taskId: string;
};

function TaskCostsSection({ taskId }: { taskId: string }) {
  const [doc, setDoc] = useState<OperationalDocumentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingLineId, setUpdatingLineId] = useState<string | null>(null);

  const load = () => {
    getOperationalDocument(taskId)
      .then(setDoc)
      .catch((e) => setError((e as Error)?.message ?? "Erreur chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    getOperationalDocument(taskId)
      .then((data) => {
        if (!mounted) return;
        setDoc(data);
      })
      .catch((e) => {
        if (!mounted) return;
        setError((e as Error)?.message ?? "Erreur chargement");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [taskId]);

  const assignToInvoiceLine = async (costLine: CostLineOut, invoiceLineId: string | null) => {
    setUpdatingLineId(costLine.id);
    try {
      await updateTaskCostLine(taskId, costLine.id, { invoice_line_id: invoiceLineId || undefined });
      load();
    } finally {
      setUpdatingLineId(null);
    }
  };

  if (loading) return <p className="text-sm text-muted">Chargement des coûts...</p>;
  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!doc) return null;

  return (
    <div className="space-y-4">
      {doc.currency_warning && (
        <p className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 rounded-md px-3 py-2">
          {doc.currency_warning}
        </p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-md border border-border bg-surface-muted/30 p-4">
          <p className="text-xs text-muted uppercase tracking-wide">Revenu (facture)</p>
          <p className="text-lg font-semibold text-text">{doc.total_revenue.toFixed(2)} $</p>
        </div>
        <div className="rounded-md border border-border bg-surface-muted/30 p-4">
          <p className="text-xs text-muted uppercase tracking-wide">Total coûts</p>
          <p className="text-lg font-semibold text-text">{doc.total_costs.toFixed(2)} $</p>
        </div>
        <div className="rounded-md border border-border bg-surface-muted/30 p-4">
          <p className="text-xs text-muted uppercase tracking-wide">Marge réelle</p>
          <p className={`text-lg font-semibold ${doc.margin >= 0 ? "text-success" : "text-danger"}`}>
            {doc.margin.toFixed(2)} $
          </p>
        </div>
      </div>
      <div className="rounded-md border border-border overflow-hidden">
        <h4 className="text-sm font-medium text-text border-b border-border bg-muted/30 px-4 py-2">Lignes de coût</h4>
        {doc.cost_lines.length === 0 ? (
          <p className="p-4 text-sm text-muted">Aucune ligne de coût pour ce dossier.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/20 text-left text-muted">
                <th className="p-3 font-medium">Type</th>
                <th className="p-3 font-medium">Source</th>
                <th className="p-3 font-medium">Libellé</th>
                <th className="p-3 font-medium">Quantité</th>
                <th className="p-3 font-medium">Coût unit.</th>
                <th className="p-3 font-medium">Total</th>
                {doc.invoice_lines && doc.invoice_lines.length > 0 && (
                  <th className="p-3 font-medium">Affecter à ligne facture</th>
                )}
              </tr>
            </thead>
            <tbody>
              {doc.cost_lines.map((cl) => (
                <tr key={cl.id} className="border-b border-border/50 hover:bg-muted/10">
                  <td className="p-3 text-text">{cl.cost_type}</td>
                  <td className="p-3 text-text">{cl.source}</td>
                  <td className="p-3 text-text">{cl.label}</td>
                  <td className="p-3 text-text">{cl.quantity}</td>
                  <td className="p-3 text-text">{cl.unit_cost} {cl.currency}</td>
                  <td className="p-3 font-medium text-text">{cl.total_amount.toFixed(2)} {cl.currency}</td>
                  {doc.invoice_lines && doc.invoice_lines.length > 0 && (
                    <td className="p-3">
                      <select
                        value={cl.invoice_line_id ?? ""}
                        onChange={(e) => assignToInvoiceLine(cl, e.target.value || null)}
                        disabled={updatingLineId === cl.id}
                        className="text-sm rounded border border-border bg-surface px-2 py-1 disabled:opacity-50"
                      >
                        <option value="">— Non affecté</option>
                        {doc.invoice_lines.map((il) => (
                          <option key={il.id} value={il.id}>{il.label || `Ligne ${il.line_order}`}</option>
                        ))}
                      </select>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function TaskDetailView({ taskId }: TaskDetailViewProps) {
  const [task, setTask] = useState<TaskDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"timeline" | "history" | "costs">("timeline");

  const refetchTask = useCallback(() => {
    getTask(taskId).then(setTask).catch(console.error);
  }, [taskId]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    getTask(taskId)
      .then((data) => {
        if (!mounted) return;
        setTask(data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to load task:", error);
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [taskId]);

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Chargement...</div>;
  }

  if (!task) {
    return <div className="p-8 text-center text-muted-foreground">Tâche introuvable</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border bg-surface p-4">
        <h2 className="text-lg font-semibold">Tâche {task.ops_family}</h2>
        <p className="text-sm text-muted-foreground">
          Statut: {task.status_ops} | Cycle: {task.status_lifecycle} | Santé: {task.health_state}
        </p>
      </div>

      <div className="flex-1 overflow-hidden">
        <Tabs
          tabs={[
            { id: "timeline", label: "Timeline" },
            { id: "history", label: "Historique" },
            { id: "costs", label: "Coûts détaillés" },
          ]}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as "timeline" | "history" | "costs")}
          variant="underline"
        />

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === "timeline" && <TaskTimeline task={task} onTaskUpdated={refetchTask} />}
          {activeTab === "history" && <TaskHistory taskId={task.id} />}
          {activeTab === "costs" && <TaskCostsSection taskId={task.id} />}
        </div>
      </div>
    </div>
  );
}
